import React, { useState, useEffect, useMemo, useCallback, useTransition } from 'react';
import { Plus, Search, CheckSquare, Square, Pencil, Trash2, Check, X, AlertCircle, GitMerge } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { supabase } from '../../../lib/supabaseClient';
import { useSettingsData } from '../../../contexts/SettingsDataContext';

const CATEGORIES = [
  { value: '', label: 'No Category' },
  { value: 'language', label: 'Language' },
  { value: 'framework', label: 'Framework' },
  { value: 'platform', label: 'Platform' },
  { value: 'process', label: 'Process' },
  { value: 'tool', label: 'Tool' },
  { value: 'certification', label: 'Certification' },
  { value: 'sfia', label: 'SFIA' },
];

const SkillsManagement = () => {
  // Use cached data from context
  const { skills: contextSkills, loading: contextLoading, refreshSkills } = useSettingsData();
  const skills = contextSkills || [];
  const loading = contextLoading && !contextSkills;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, pending
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Use transition for non-blocking UI updates during filtering
  const [isPending, startTransition] = useTransition();

  // Selection state
  const [selectedSkillIds, setSelectedSkillIds] = useState([]);

  // Add/Edit state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: '', status: 'active', mapped_to_skill_id: null });

  // Delete state
  const [skillToDelete, setSkillToDelete] = useState(null);

  // Merge state
  const [skillToMerge, setSkillToMerge] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [merging, setMerging] = useState(false);

  // Bulk map state
  const [showBulkMapDialog, setShowBulkMapDialog] = useState(false);
  const [bulkMapTargetId, setBulkMapTargetId] = useState('');
  const [bulkMapping, setBulkMapping] = useState(false);

  // Bulk delete state
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Feedback state
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Debounce search term to reduce filtering operations
  useEffect(() => {
    const timer = setTimeout(() => {
      // Use transition to make filtering non-blocking
      startTransition(() => {
        setDebouncedSearchTerm(searchTerm);
      });
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Auto-dismiss messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Optimized filtered and sorted skills - single pass filtering
  const filteredSkills = useMemo(() => {
    const lowerSearch = debouncedSearchTerm.toLowerCase();

    // Single pass filter - check all conditions at once
    const filtered = skills.filter(skill => {
      // Status filter
      if (statusFilter !== 'all' && skill.status !== statusFilter) {
        return false;
      }

      // Category filter
      if (categoryFilter !== 'all') {
        if (categoryFilter === 'none' && skill.category) {
          return false;
        }
        if (categoryFilter !== 'none' && skill.category !== categoryFilter) {
          return false;
        }
      }

      // Search filter (only if search term exists)
      if (debouncedSearchTerm && !skill.name.toLowerCase().includes(lowerSearch)) {
        return false;
      }

      return true;
    });

    // Sort: Pending skills first, then alphabetically by name
    filtered.sort((a, b) => {
      // Pending skills always come first
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;

      // Within same status, sort alphabetically
      return a.name.localeCompare(b.name);
    });

    return filtered;
  }, [skills, statusFilter, categoryFilter, debouncedSearchTerm]);

  const handleAddSkill = async () => {
    // Validation: if mapping status, mapped_to_skill_id is required
    if (formData.status === 'mapping' && !formData.mapped_to_skill_id) {
      setErrorMessage('Please select a skill to map to');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/skills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          category: formData.category || null,
          status: formData.status,
          mapped_to_skill_id: formData.status === 'mapping' ? formData.mapped_to_skill_id : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create skill');
      }

      setSuccessMessage(`Skill "${data.skill.name}" created successfully!`);
      setShowAddDialog(false);
      setFormData({ name: '', category: '', status: 'active', mapped_to_skill_id: null });
      await refreshSkills();
    } catch (error) {
      console.error('Error adding skill:', error);
      setErrorMessage(error.message);
    }
  };

  const handleUpdateSkill = async () => {
    // Validation: if mapping status, mapped_to_skill_id is required
    if (formData.status === 'mapping' && !formData.mapped_to_skill_id) {
      setErrorMessage('Please select a skill to map to');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/${editingSkill.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          category: formData.category || null,
          status: formData.status,
          mapped_to_skill_id: formData.status === 'mapping' ? formData.mapped_to_skill_id : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update skill');
      }

      setSuccessMessage(`Skill "${data.skill.name}" updated successfully!`);
      setEditingSkill(null);
      setFormData({ name: '', category: '', status: 'active', mapped_to_skill_id: null });
      await refreshSkills();
    } catch (error) {
      console.error('Error updating skill:', error);
      setErrorMessage(error.message);
    }
  };

  const handleDeleteSkill = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/${skillToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete skill');
      }

      setSuccessMessage(`Skill "${skillToDelete.name}" deleted successfully!`);
      setSkillToDelete(null);
      await refreshSkills();
    } catch (error) {
      console.error('Error deleting skill:', error);
      setErrorMessage(error.message);
    }
  };

  const handleMergeSkill = async () => {
    if (!mergeTargetId) {
      setErrorMessage('Please select a target skill');
      return;
    }

    try {
      setMerging(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/${skillToMerge.id}/merge`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targetSkillId: mergeTargetId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to merge skill');
      }

      const targetSkill = skills.find(s => s.id === mergeTargetId);
      setSuccessMessage(
        `Successfully merged "${skillToMerge.name}" into "${targetSkill?.name}". ` +
        `Updated ${data.updatedCounts.rfqMatchProfiles} RFQs and ${data.updatedCounts.candidateSkills} candidates.`
      );

      setSkillToMerge(null);
      setMergeTargetId('');
      await refreshSkills();
    } catch (error) {
      console.error('Error merging skill:', error);
      setErrorMessage(error.message);
    } finally {
      setMerging(false);
    }
  };

  const handleBulkApprove = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/bulk-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          skill_ids: selectedSkillIds,
          status: 'active'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to approve skills');
      }

      setSuccessMessage(`Successfully approved ${data.updated} skill(s)!`);
      setSelectedSkillIds([]);
      await refreshSkills();
    } catch (error) {
      console.error('Error approving skills:', error);
      setErrorMessage(error.message);
    }
  };

  const handleBulkMap = async () => {
    if (!bulkMapTargetId) {
      setErrorMessage('Please select a target skill');
      return;
    }

    try {
      setBulkMapping(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/bulk-map`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          skill_ids: selectedSkillIds,
          mapped_to_skill_id: bulkMapTargetId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to map skills');
      }

      const targetSkill = skills.find(s => s.id === bulkMapTargetId);
      setSuccessMessage(
        `Successfully mapped ${data.updated} skill(s) to "${targetSkill?.name}"`
      );

      setShowBulkMapDialog(false);
      setBulkMapTargetId('');
      setSelectedSkillIds([]);
      await refreshSkills();
    } catch (error) {
      console.error('Error mapping skills:', error);
      setErrorMessage(error.message);
    } finally {
      setBulkMapping(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setBulkDeleting(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          skill_ids: selectedSkillIds
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete skills');
      }

      setSuccessMessage(
        `Successfully deleted ${data.deleted} skill(s)`
      );

      setShowBulkDeleteDialog(false);
      setSelectedSkillIds([]);
      await refreshSkills();
    } catch (error) {
      console.error('Error deleting skills:', error);
      setErrorMessage(error.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSkillSelection = useCallback((skillId) => {
    setSelectedSkillIds(prev =>
      prev.includes(skillId) ? prev.filter(id => id !== skillId) : [...prev, skillId]
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedSkillIds.length === filteredSkills.length) {
      setSelectedSkillIds([]);
    } else {
      setSelectedSkillIds(filteredSkills.map(skill => skill.id));
    }
  }, [selectedSkillIds.length, filteredSkills]);

  const openEditDialog = useCallback((skill) => {
    setEditingSkill(skill);
    setFormData({
      name: skill.name,
      category: skill.category || '',
      status: skill.status,
      mapped_to_skill_id: skill.mapped_to_skill_id || null
    });
  }, []);

  const openMergeDialog = useCallback((skill) => {
    setSkillToMerge(skill);
    setMergeTargetId('');
  }, []);

  const closeDialog = useCallback(() => {
    setShowAddDialog(false);
    setEditingSkill(null);
    setFormData({ name: '', category: '', status: 'active', mapped_to_skill_id: null });
  }, []);

  // Memoize pending count to avoid recalculating on every render
  const pendingCount = useMemo(
    () => skills.filter(s => s.status === 'pending').length,
    [skills]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <Alert className="bg-green-50 border-green-200">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <CardTitle>Skills Master Database</CardTitle>
                {pendingCount > 0 && (
                  <Badge variant="outline">{pendingCount} pending approval</Badge>
                )}
              </div>
              <CardDescription>
                Manage skills used throughout the platform.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {selectedSkillIds.length > 0 && (
                <>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleBulkApprove}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Approve Selected ({selectedSkillIds.length})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkMapDialog(true)}
                  >
                    <GitMerge className="h-4 w-4 mr-2" />
                    Map Selected ({selectedSkillIds.length})
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowBulkDeleteDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedSkillIds.length})
                  </Button>
                </>
              )}
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Skill
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="mapping">Mapping</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="all">All Categories</option>
              <option value="none">No Category</option>
              {CATEGORIES.slice(1).map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>

            <div className="text-sm text-muted-foreground flex items-center">
              Showing {filteredSkills.length} of {skills.length} skills
            </div>
          </div>

          {/* Skills Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center justify-center w-full"
                  >
                    {selectedSkillIds.length === filteredSkills.length && filteredSkills.length > 0 ? (
                      <CheckSquare className="h-4 w-4 text-primary" />
                    ) : selectedSkillIds.length > 0 ? (
                      <CheckSquare className="h-4 w-4 text-primary opacity-50" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSkills.map((skill) => (
                <TableRow key={skill.id}>
                  <TableCell>
                    <button
                      onClick={() => toggleSkillSelection(skill.id)}
                      className="flex items-center justify-center w-full"
                    >
                      {selectedSkillIds.includes(skill.id) ? (
                        <CheckSquare className="h-4 w-4 text-primary" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium">{skill.name}</TableCell>
                  <TableCell>
                    {skill.category ? (
                      <Badge variant="outline">{skill.category}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={skill.status === 'active' ? 'default' : 'secondary'}
                      className={
                        skill.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                        skill.status === 'mapping' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                        skill.status === 'rejected' ? 'bg-slate-100 text-slate-600 border-slate-300' : ''
                      }
                    >
                      {skill.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {skill.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openMergeDialog(skill)}
                          title="Merge into existing skill"
                        >
                          <GitMerge className="h-4 w-4 text-blue-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(skill)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSkillToDelete(skill)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredSkills.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No skills found matching your filters</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || editingSkill !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSkill ? 'Edit Skill' : 'Add New Skill'}</DialogTitle>
            <DialogDescription>
              {editingSkill ? 'Update skill details' : 'Create a new skill in the master database'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Skill Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Python, React, AWS"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <select
                value={formData.status}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  setFormData({
                    ...formData,
                    status: newStatus,
                    // Clear mapped_to_skill_id if not mapping
                    mapped_to_skill_id: newStatus === 'mapping' ? formData.mapped_to_skill_id : null
                  });
                }}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="mapping">Mapping (alias to canonical skill)</option>
                {editingSkill && <option value="rejected">Rejected</option>}
              </select>
            </div>

            {/* Map to selector - only show when status is 'mapping' */}
            {formData.status === 'mapping' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Map to Canonical Skill *</label>
                <select
                  value={formData.mapped_to_skill_id || ''}
                  onChange={(e) => setFormData({ ...formData, mapped_to_skill_id: e.target.value || null })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">-- Select a canonical skill --</option>
                  {skills
                    .filter(s => s.status === 'active' && s.id !== editingSkill?.id)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(skill => (
                      <option key={skill.id} value={skill.id}>
                        {skill.name} {skill.category ? `(${skill.category})` : ''}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  When this alias is matched, it will be treated as the canonical skill
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={editingSkill ? handleUpdateSkill : handleAddSkill}
              disabled={!formData.name.trim()}
            >
              {editingSkill ? 'Update' : 'Create'} Skill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={skillToDelete !== null} onOpenChange={(open) => !open && setSkillToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Skill?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{skillToDelete?.name}"? This action cannot be undone.
              {' '}If the skill is assigned to candidates, deletion will fail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkillToDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteSkill}>
              Delete Skill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Skill Dialog */}
      <Dialog open={skillToMerge !== null} onOpenChange={(open) => !open && setSkillToMerge(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-blue-600" />
              Merge Skill
            </DialogTitle>
            <DialogDescription>
              Merge "{skillToMerge?.name}" into an existing active skill. All references (RFQs, candidates) will be updated automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Source Skill (Pending)</label>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{skillToMerge?.name}</span>
                  <Badge className="bg-yellow-100 text-yellow-800">pending</Badge>
                </div>
                {skillToMerge?.category && (
                  <span className="text-xs text-muted-foreground">{skillToMerge.category}</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="target-skill" className="text-sm font-medium">
                Target Skill (Active) <span className="text-destructive">*</span>
              </label>
              <select
                id="target-skill"
                value={mergeTargetId}
                onChange={(e) => setMergeTargetId(e.target.value)}
                className="w-full p-2 border border-input rounded-md bg-background"
                disabled={merging}
              >
                <option value="">Select a skill to merge into...</option>
                {skills
                  .filter(s => s.status === 'active')
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(skill => (
                    <option key={skill.id} value={skill.id}>
                      {skill.name} {skill.category ? `(${skill.category})` : ''}
                    </option>
                  ))}
              </select>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>This action will:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Update all RFQs using this skill</li>
                  <li>Update all candidates with this skill</li>
                  <li>Mark "{skillToMerge?.name}" as rejected with mapping</li>
                  <li>Future AI extractions of this phrase will auto-map to the target</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSkillToMerge(null)}
              disabled={merging}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMergeSkill}
              disabled={merging || !mergeTargetId}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {merging ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Merging...
                </>
              ) : (
                <>
                  <GitMerge className="h-4 w-4 mr-2" />
                  Merge Skills
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Map Dialog */}
      <Dialog open={showBulkMapDialog} onOpenChange={(open) => !open && setShowBulkMapDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-blue-600" />
              Bulk Map Skills
            </DialogTitle>
            <DialogDescription>
              Map {selectedSkillIds.length} selected skill{selectedSkillIds.length > 1 ? 's' : ''} to a single canonical skill. All will be set to "mapping" status.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Selected Skills ({selectedSkillIds.length})</label>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-md max-h-32 overflow-y-auto">
                <div className="space-y-1">
                  {selectedSkillIds.map(skillId => {
                    const skill = skills.find(s => s.id === skillId);
                    return skill ? (
                      <div key={skillId} className="text-sm">
                        • {skill.name} {skill.category ? <span className="text-muted-foreground">({skill.category})</span> : ''}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="bulk-target-skill" className="text-sm font-medium">
                Target Skill (Active) <span className="text-destructive">*</span>
              </label>
              <select
                id="bulk-target-skill"
                value={bulkMapTargetId}
                onChange={(e) => setBulkMapTargetId(e.target.value)}
                className="w-full p-2 border border-input rounded-md bg-background"
                disabled={bulkMapping}
              >
                <option value="">Select a canonical skill...</option>
                {skills
                  .filter(s => s.status === 'active' && !selectedSkillIds.includes(s.id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(skill => (
                    <option key={skill.id} value={skill.id}>
                      {skill.name} {skill.category ? `(${skill.category})` : ''}
                    </option>
                  ))}
              </select>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>This will:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Set all selected skills to "mapping" status</li>
                  <li>Map them to the chosen canonical skill</li>
                  <li>Future references will use the canonical skill</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkMapDialog(false)}
              disabled={bulkMapping}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkMap}
              disabled={bulkMapping || !bulkMapTargetId}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {bulkMapping ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Mapping...
                </>
              ) : (
                <>
                  <GitMerge className="h-4 w-4 mr-2" />
                  Map {selectedSkillIds.length} Skill{selectedSkillIds.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={(open) => !open && setShowBulkDeleteDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Skills
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedSkillIds.length} selected skill{selectedSkillIds.length > 1 ? 's' : ''}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Skills to Delete ({selectedSkillIds.length})</label>
              <div className="p-3 bg-red-50 border border-red-200 rounded-md max-h-32 overflow-y-auto">
                <div className="space-y-1">
                  {selectedSkillIds.map(skillId => {
                    const skill = skills.find(s => s.id === skillId);
                    return skill ? (
                      <div key={skillId} className="text-sm text-red-900">
                        • {skill.name} {skill.category ? <span className="text-red-700">({skill.category})</span> : ''}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </div>

            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>Warning:</strong> Skills that are assigned to candidates or used in RFQ match profiles cannot be deleted.
                The operation will fail if any selected skills are in use.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteDialog(false)}
              disabled={bulkDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              variant="destructive"
            >
              {bulkDeleting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete {selectedSkillIds.length} Skill{selectedSkillIds.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SkillsManagement;
